import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { 
  FiSearch, 
  FiFilter, 
  FiRefreshCw, 
  FiChevronUp,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiDollarSign,
  FiAlertTriangle,
  FiClock,
  FiCheckCircle,
  FiX,
  FiEye,
  FiFileText
} from 'react-icons/fi';
import instanciaAxios from '../../miscelaneo/instanciaAxios';
import SpinnerCargando from '../util/SpinnerCargando';
import ModalDetalleCompra from './ModalDetalleCompra';
import ModalHistorialPagosCompra from './ModalHistorialPagosCompra';
import { roundTo2 } from '../../miscelaneo/monetario';
import InputClientes from '../util/Inputs/InputClientes';
import FuncionesGenerales from '../util/FuncionesGenerales';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import PlantillaModal from '../util/PlantillaModal';
import TablaEntidad from '../util/tablas/TablaEntidad';

// =====================================================
// COMPONENTE: Tarjeta de Resumen
// =====================================================
function TarjetaResumen({ titulo, valor, subtitulo, colorBorde, colorTexto, activo, onClick }) {
  return (
    <div 
      className={`bg-white rounded-xl border shadow-md p-4 border-l-4 ${colorBorde} transition-all cursor-pointer hover:shadow-lg ${
        activo ? 'ring-2 ring-blue-400 ring-offset-1' : 'border-gray-300'
      }`}
      onClick={onClick}
    >
      <p className="text-sm text-gray-500">{titulo}</p>
      <p className={`text-2xl font-bold mt-1 ${colorTexto}`}>{valor}</p>
      {subtitulo && <p className="text-xs text-gray-400 mt-1">{subtitulo}</p>}
    </div>
  );
}

// =====================================================
// Formatear moneda
// =====================================================
function formatearMoneda(valor) {
  return roundTo2(parseFloat(valor) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

// =====================================================
// Helper: estado de pago badge
// =====================================================
function BadgeEstadoPago({ estado }) {
  const config = {
    pagada:    { clase: 'bg-green-100 text-green-700', texto: 'Pagada', icono: FiCheckCircle },
    parcial:   { clase: 'bg-yellow-100 text-yellow-700', texto: 'Parcial', icono: FiClock },
    pendiente: { clase: 'bg-red-100 text-red-700', texto: 'Pendiente', icono: FiAlertTriangle }
  };
  const c = config[estado] || config.pendiente;
  const Icono = c.icono;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.clase}`}>
      <Icono size={12} />
      {c.texto}
    </span>
  );
}

// =====================================================
// COMPONENTE PRINCIPAL: Pantalla Cuentas por Pagar
// =====================================================
export default function PantallaCxP() {
  const { usuario } = useContextoSesion();
  const { formatoMoneda: formatoMonedaUtil } = FuncionesGenerales(usuario);
  
  // Estado de datos
  const [compras, setCompras] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  
  // Estado de filtros (API-driven)
  const [filtros, setFiltros] = useState({
    ncf: '',
    id_entidad_proveedor: '',
    proveedor_nombre: '',
    estado: '',
    estado_pago: 'todas',
    fecha_desde: '',
    fecha_hasta: '',
    ordenar_por: 'fecha_hora',
    orden: 'DESC'
  });

  // Estado de filtros temporales (UI-driven)
  const [filtrosTemporales, setFiltrosTemporales] = useState({
    ncf: '',
    id_entidad_proveedor: '',
    proveedor_nombre: '',
    estado: '',
    fecha_desde: '',
    fecha_hasta: '',
    ordenar_por: 'fecha_hora',
    orden: 'DESC'
  });
  
  // Estado de paginación
  const [paginacion, setPaginacion] = useState({
    limite: 25,
    offset: 0,
    total: 0
  });
  
  // Estado de UI
  const [filtrosExpandidos, setFiltrosExpandidos] = useState(false);
  const [compraSeleccionada, setCompraSeleccionada] = useState(null);
  const [modalDetalleAbierto, setModalDetalleAbierto] = useState(false);
  const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
  const [mostrarModalProveedores, setMostrarModalProveedores] = useState(false);

  // Cargar datos
  const cargarDatos = useCallback(async () => {
    try {
      setCargando(true);
      
      const params = {
        limite: paginacion.limite,
        offset: paginacion.offset
      };
      
      if (filtros.ncf) params.ncf = filtros.ncf;
      if (filtros.id_entidad_proveedor) params.id_entidad_proveedor = filtros.id_entidad_proveedor;
      if (filtros.estado) params.estado = filtros.estado;
      if (filtros.estado_pago && filtros.estado_pago !== 'todas') params.estado_pago = filtros.estado_pago;
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      if (filtros.ordenar_por) params.ordenar_por = filtros.ordenar_por;
      if (filtros.orden) params.orden = filtros.orden;
      
      const [resumenResp, comprasResp] = await Promise.all([
        instanciaAxios.get('/cxp/resumen'),
        instanciaAxios.get('/cxp', { params })
      ]);

      setResumen(resumenResp.data);
      setCompras(comprasResp.data.compras);
      setPaginacion(prev => ({ ...prev, total: comprasResp.data.total }));
      
    } catch (error) {
      console.error('Error al cargar cuentas por pagar:', error);
      toast.error('Error al cargar datos');
    } finally {
      setCargando(false);
    }
  }, [filtros, paginacion.limite, paginacion.offset]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Helpers
  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getEstadoVencimientoConfig = (estado) => {
    switch (estado) {
      case 'vencida':
        return { clase: 'bg-red-100 text-red-700', texto: 'Vencida', icono: FiAlertTriangle };
      case 'por_vencer':
        return { clase: 'bg-amber-100 text-amber-700', texto: 'Por vencer', icono: FiClock };
      default:
        return { clase: 'bg-green-100 text-green-700', texto: 'Vigente', icono: FiCheckCircle };
    }
  };

  // Handlers
  const handleOrdenar = (columna) => {
    setFiltros(prev => ({
      ...prev,
      ordenar_por: columna,
      orden: prev.ordenar_por === columna && prev.orden === 'ASC' ? 'DESC' : 'ASC'
    }));
    setPaginacion(prev => ({ ...prev, offset: 0 }));
  };

  const handleBuscar = () => {
    setFiltros(prev => ({
      ...prev,
      ...filtrosTemporales
    }));
    setPaginacion(prev => ({ ...prev, offset: 0 }));
  };

  const handleLimpiarFiltros = () => {
    const defaults = {
      ncf: '',
      id_entidad_proveedor: '',
      proveedor_nombre: '',
      estado: '',
      estado_pago: 'todas',
      fecha_desde: '',
      fecha_hasta: '',
      ordenar_por: 'fecha_hora',
      orden: 'DESC'
    };
    setFiltros(defaults);
    setFiltrosTemporales({
      ncf: '',
      id_entidad_proveedor: '',
      proveedor_nombre: '',
      estado: '',
      fecha_desde: '',
      fecha_hasta: '',
      ordenar_por: 'fecha_hora',
      orden: 'DESC'
    });
    setPaginacion(prev => ({ ...prev, offset: 0 }));
  };

  const handleEstadoPagoClick = (estado) => {
    const nuevoEstado = filtros.estado_pago === estado ? 'todas' : estado;
    setFiltros(prev => ({ ...prev, estado_pago: nuevoEstado }));
    setPaginacion(prev => ({ ...prev, offset: 0 }));
  };

  const handleProveedorSeleccionado = (proveedor) => {
    setFiltrosTemporales(prev => ({ 
      ...prev, 
      id_entidad_proveedor: proveedor?.id_entidad || '',
      proveedor_nombre: proveedor ? `${proveedor.nombre} ${proveedor.apellido || ''}`.trim() : ''
    }));
    setMostrarModalProveedores(false);
  };

  const abrirModalDetalle = (compra) => {
    setCompraSeleccionada(compra);
    setModalDetalleAbierto(true);
  };

  const abrirModalHistorial = (compra) => {
    setCompraSeleccionada(compra);
    setModalHistorialAbierto(true);
  };

  // Indicador de ordenamiento
  const OrdenIndicador = ({ columna }) => {
    if (filtros.ordenar_por !== columna) return null;
    return filtros.orden === 'ASC' 
      ? <FiChevronUp className="inline ml-1" size={14} />
      : <FiChevronDown className="inline ml-1" size={14} />;
  };

  // Verificar si hay filtros activos
  const hayFiltrosActivos = useMemo(() => {
    return filtros.ncf || filtros.id_entidad_proveedor || filtros.estado ||
           filtros.fecha_desde || filtros.fecha_hasta ||
           (filtros.estado_pago && filtros.estado_pago !== 'todas');
  }, [filtros]);

  if (cargando && !resumen) {
    return <SpinnerCargando />;
  }

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="flex justify-center py-10">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border border-gray-100 p-12 w-full max-w-7xl mx-10">
          <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="font-cascadia font-bold text-2xl">Cuentas por Pagar</h1>
          <p className="text-gray-500">Gestione las compras pendientes de pago, registre pagos y consulte detalles.</p>
        </div>
        <button
          onClick={cargarDatos}
          disabled={cargando}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          title="Actualizar"
        >
          <FiRefreshCw size={20} className={cargando ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Resumen */}
      {resumen && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <TarjetaResumen
            titulo="Total Compras"
            valor={`RD$ ${formatearMoneda(resumen.resumen.total_comprado)}`}
            subtitulo={`${resumen.resumen.cantidad_compras} compras`}
            colorBorde="border-l-color-primario"
            colorTexto="text-gray-800"
            activo={filtros.estado_pago === 'todas'}
            onClick={() => handleEstadoPagoClick('todas')}
          />
          <TarjetaResumen
            titulo="Pendientes"
            valor={`RD$ ${formatearMoneda(resumen.resumen.total_sin_pago)}`}
            subtitulo={`${resumen.resumen.cantidad_sin_pago} compras`}
            colorBorde="border-l-red-500"
            colorTexto="text-red-600"
            activo={filtros.estado_pago === 'pendiente'}
            onClick={() => handleEstadoPagoClick('pendiente')}
          />
          <TarjetaResumen
            titulo="Parciales"
            valor={`RD$ ${formatearMoneda(resumen.resumen.total_parcial_pendiente)}`}
            subtitulo={`${resumen.resumen.cantidad_parciales} compras`}
            colorBorde="border-l-amber-500"
            colorTexto="text-amber-600"
            activo={filtros.estado_pago === 'parcial'}
            onClick={() => handleEstadoPagoClick('parcial')}
          />
          <TarjetaResumen
            titulo="Pagadas"
            valor={`RD$ ${formatearMoneda(resumen.resumen.total_pagado_completo)}`}
            subtitulo={`${resumen.resumen.cantidad_pagadas} compras`}
            colorBorde="border-l-green-500"
            colorTexto="text-green-600"
            activo={filtros.estado_pago === 'pagada'}
            onClick={() => handleEstadoPagoClick('pagada')}
          />
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-300 shadow-md overflow-hidden">
        <div className="p-4 flex flex-wrap items-center gap-4">
          <select
            className="px-4 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors min-w-[180px]"
            value={filtros.estado_pago}
            onChange={(e) => {
              setFiltros(prev => ({ ...prev, estado_pago: e.target.value }));
              setPaginacion(prev => ({ ...prev, offset: 0 }));
            }}
          >
            <option value="todas">Todas las compras</option>
            <option value="pendiente">Pendientes de pago</option>
            <option value="parcial">Pago parcial</option>
            <option value="pagada">Pagadas</option>
          </select>

          <select
            className="px-4 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors min-w-[160px]"
            value={filtrosTemporales.estado}
            onChange={(e) => {
              setFiltrosTemporales(prev => ({ ...prev, estado: e.target.value }));
            }}
          >
            <option value="">Vencimiento</option>
            <option value="vencida">Vencidas</option>
            <option value="por_vencer">Por vencer</option>
            <option value="vigente">Vigentes</option>
          </select>
          
          <button
            onClick={() => setFiltrosExpandidos(!filtrosExpandidos)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-xl transition-colors ${
              filtrosExpandidos || hayFiltrosActivos
                ? 'border-color-primario text-color-primario bg-blue-50'
                : 'border-gray-400 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <FiFilter size={18} />
            Filtros
            {hayFiltrosActivos && (
              <span className="w-2 h-2 bg-color-primario rounded-full" />
            )}
          </button>
          
          <button
            onClick={handleBuscar}
            className="flex items-center gap-2 px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-green-700 transition-colors"
          >
            <FiSearch size={16} />
            Buscar
          </button>
          
          {hayFiltrosActivos && (
            <button
              onClick={handleLimpiarFiltros}
              className="flex items-center gap-1 px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <FiX size={16} />
              Limpiar
            </button>
          )}
        </div>
        
        {/* Filtros expandidos */}
        {filtrosExpandidos && (
          <div className="px-4 pb-4 pt-2 border-t border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">NCF</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario"
                placeholder="NCF exacto..."
                value={filtrosTemporales.ncf}
                onChange={(e) => {
                  setFiltrosTemporales(prev => ({ ...prev, ncf: e.target.value }));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleBuscar();
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Proveedor</label>
              <InputClientes
                onClick={() => setMostrarModalProveedores(true)}
                valor={filtrosTemporales.id_entidad_proveedor}
                nombreCliente={filtrosTemporales.proveedor_nombre || ''}
                placeholder="Buscar proveedor..."
                editable={true}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha Desde</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario"
                value={filtrosTemporales.fecha_desde}
                onChange={(e) => {
                  setFiltrosTemporales(prev => ({ ...prev, fecha_desde: e.target.value }));
                }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha Hasta</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario"
                value={filtrosTemporales.fecha_hasta}
                onChange={(e) => {
                  setFiltrosTemporales(prev => ({ ...prev, fecha_hasta: e.target.value }));
                }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Ordenar Por</label>
              <select
                className="w-full px-3 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario"
                value={filtrosTemporales.ordenar_por}
                onChange={(e) => {
                  setFiltrosTemporales(prev => ({ ...prev, ordenar_por: e.target.value }));
                }}
              >
                <option value="fecha_hora">Fecha Creación</option>
                <option value="fecha_vencimiento">Fecha Vencimiento</option>
                <option value="balance">Balance</option>
                <option value="monto_total">Monto Total</option>
                <option value="ncf">NCF</option>
                <option value="proveedor_nombre">Proveedor</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-300 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-color-primario text-white">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                  onClick={() => handleOrdenar('ncf')}
                >
                  NCF <OrdenIndicador columna="ncf" />
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                  onClick={() => handleOrdenar('proveedor_nombre')}
                >
                  Proveedor <OrdenIndicador columna="proveedor_nombre" />
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                  onClick={() => handleOrdenar('fecha_hora')}
                >
                  Fecha <OrdenIndicador columna="fecha_hora" />
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Estado Pago
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Vencimiento
                </th>
                <th 
                  className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                  onClick={() => handleOrdenar('monto_total')}
                >
                  Monto <OrdenIndicador columna="monto_total" />
                </th>
                <th 
                  className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                  onClick={() => handleOrdenar('balance')}
                >
                  Balance <OrdenIndicador columna="balance" />
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Pagos
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {compras.map((compra) => {
                const estadoVencConfig = getEstadoVencimientoConfig(compra.estado_vencimiento);
                const IconoEstadoVenc = estadoVencConfig.icono;
                
                return (
                  <tr 
                    key={compra.id_compra} 
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => abrirModalDetalle(compra)}
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-gray-900">{compra.ncf || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 truncate max-w-[200px]">
                          {compra.proveedor_nombre || '—'}
                        </p>
                        {compra.proveedor_rnc && (
                          <p className="text-xs text-gray-500">{compra.proveedor_rnc}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatearFecha(compra.fecha_hora)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <BadgeEstadoPago estado={compra.estado_pago} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {parseFloat(compra.balance) > 0 ? (
                        <div className="flex flex-col items-center">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${estadoVencConfig.clase}`}>
                            <IconoEstadoVenc size={12} />
                            {estadoVencConfig.texto}
                          </span>
                          {compra.dias_vencida > 0 && (
                            <span className="text-red-600 text-xs font-medium mt-0.5">
                              {compra.dias_vencida}d vencida
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600">
                      RD$ {formatearMoneda(compra.monto_total)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${parseFloat(compra.balance) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        RD$ {formatearMoneda(compra.balance)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {parseInt(compra.cantidad_pagos) > 0 ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirModalHistorial(compra);
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-color-primario hover:bg-blue-50 rounded transition-colors"
                        >
                          <FiEye size={14} />
                          {compra.cantidad_pagos}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirModalDetalle(compra);
                        }}
                        className="px-3 py-1.5 bg-color-primario text-white text-sm font-medium rounded-xl hover:bg-opacity-90 transition-colors inline-flex items-center gap-1"
                      >
                        <FiFileText size={14} />
                        Ver
                      </button>
                    </td>
                  </tr>
                );
              })}
              
              {compras.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <FiFileText className="mx-auto text-gray-300 mb-3" size={48} />
                    <p className="text-gray-500">No se encontraron compras</p>
                    {hayFiltrosActivos && (
                      <button
                        onClick={handleLimpiarFiltros}
                        className="mt-3 text-color-primario hover:underline font-medium"
                      >
                        Limpiar filtros
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {paginacion.total > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <p className="text-sm text-gray-600">
                Mostrando {paginacion.offset + 1} - {Math.min(paginacion.offset + paginacion.limite, paginacion.total)} de {paginacion.total}
              </p>
              <select
                className="px-2 py-1 border border-gray-300 rounded text-sm"
                value={paginacion.limite}
                onChange={(e) => setPaginacion(prev => ({ ...prev, limite: parseInt(e.target.value), offset: 0 }))}
              >
                <option value={10}>10 por página</option>
                <option value={25}>25 por página</option>
                <option value={50}>50 por página</option>
                <option value={100}>100 por página</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPaginacion(prev => ({ ...prev, offset: 0 }))}
                disabled={paginacion.offset === 0}
                className="p-2 border border-gray-300 rounded-xl disabled:opacity-50 hover:bg-gray-100 transition-colors"
                title="Primera página"
              >
                <FiChevronsLeft size={16} />
              </button>
              <button
                onClick={() => setPaginacion(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limite) }))}
                disabled={paginacion.offset === 0}
                className="px-3 py-2 border border-gray-300 rounded-xl disabled:opacity-50 hover:bg-gray-100 transition-colors flex items-center gap-1"
              >
                <FiChevronLeft size={16} />
                Anterior
              </button>
              <button
                onClick={() => setPaginacion(prev => ({ ...prev, offset: prev.offset + prev.limite }))}
                disabled={paginacion.offset + paginacion.limite >= paginacion.total}
                className="px-3 py-2 border border-gray-300 rounded-xl disabled:opacity-50 hover:bg-gray-100 transition-colors flex items-center gap-1"
              >
                Siguiente
                <FiChevronRight size={16} />
              </button>
              <button
                onClick={() => setPaginacion(prev => ({ 
                  ...prev, 
                  offset: Math.floor((prev.total - 1) / prev.limite) * prev.limite 
                }))}
                disabled={paginacion.offset + paginacion.limite >= paginacion.total}
                className="p-2 border border-gray-300 rounded-xl disabled:opacity-50 hover:bg-gray-100 transition-colors"
                title="Última página"
              >
                <FiChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

          </div>
        </div>
      </div>

      {/* Modal de Detalle de Compra */}
      <ModalDetalleCompra
        abierto={modalDetalleAbierto}
        onCerrar={() => {
          setModalDetalleAbierto(false);
          setCompraSeleccionada(null);
        }}
        idCompra={compraSeleccionada?.id_compra}
        onCompraActualizada={cargarDatos}
      />

      {/* Modal de Historial de Pagos de Compra */}
      <ModalHistorialPagosCompra
        abierto={modalHistorialAbierto}
        onCerrar={() => {
          setModalHistorialAbierto(false);
          setCompraSeleccionada(null);
        }}
        compra={compraSeleccionada}
        onPagoAnulado={cargarDatos}
      />

      {/* Modal para seleccionar proveedor */}
      <PlantillaModal 
        mostrar={mostrarModalProveedores} 
        onClose={() => setMostrarModalProveedores(false)}
        titulo="Seleccionar Proveedor"
      >
        <TablaEntidad
          tipoEntidad={2}
          descripcion="Seleccione un proveedor"
          setEntidadSeleccionada={handleProveedorSeleccionado}
        />
      </PlantillaModal>
    </div>
  );
}



